import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Divider,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Store as StoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  List as ListIcon,
} from "@mui/icons-material";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { formHookInputHelper } from "utils/formHookField";
import CancelShipmentModal from "./CancelShipmentModal";
import { useTheme } from "@mui/material/styles";
import {
  useGetCancelEligibleShipmentsQuery,
  useLazyGetActiveVendorProfilesListQuery,
} from "store/services/vendorProfileApi";
import { useLazyListCustomerWithMinimalDataQuery } from "store/services/customerApi";
import { useLazyListShipmentForBulkCancelsQuery } from "store/services/shipmentApi";

const INITIAL_VALUE = {
  shipments: [],
  vendor: null,
  customer: null,
  reason: "",
};

// Simple ObjectId validation
const isValidObjectId = (id) => /^[0-9a-fA-F]{24}$/.test(id);

export const BulkCancelForm = ({ isBusy, onSubmit }) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [lastCancellationCount, setLastCancellationCount] = React.useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);
  const [selectionMode, setSelectionMode] = React.useState("all");

  const { control, setValue, watch, reset, formState: { errors } } = useForm({
    defaultValues: INITIAL_VALUE,
  });

  const selectedVendor = watch("vendor");
  const selectedCustomer = watch("customer");
  const selectedShipments = watch("shipments");
  const reason = watch("reason");

  const [
    fetchVendors,
    {
      data: vendorList = [],
      isLoading: loadingVendors,
      isFetching: fetchingVendors,
    },
  ] = useLazyGetActiveVendorProfilesListQuery();

  const [
    fetchCustomers,
    {
      data: customerList = { data: [] },
      isLoading: loadingCustomers,
      isFetching: fetchingCustomers,
    },
  ] = useLazyListCustomerWithMinimalDataQuery();

  const [
    fetchAllShipments,
    {
      data: allShipmentsRes,
      isLoading: isLoadingAllShipments,
      isFetching: fetchingAllShipments,
    },
  ] = useLazyListShipmentForBulkCancelsQuery();

  const {
    data: shipmentForBulkRes,
    isLoading: isLoadingShipmentForBulk,
    isFetching: fetchingShipmentForBulk,
    refetch: refetchShipments,
  } = useGetCancelEligibleShipmentsQuery(
    {
      vendorId:
        selectionMode === "vendor" &&
          selectedVendor?._id &&
          isValidObjectId(selectedVendor._id)
          ? selectedVendor._id
          : null,
      customerId:
        selectionMode === "customer" &&
          selectedCustomer?._id &&
          isValidObjectId(selectedCustomer._id)
          ? selectedCustomer._id
          : null,
    },
    { skip: (selectionMode === "vendor" && !selectedVendor?._id) || (selectionMode === "customer" && !selectedCustomer?._id) }
  );

  const shipmentsList = React.useMemo(() => {
    if (selectionMode === "all") {
      return Array.isArray(allShipmentsRes)
        ? allShipmentsRes
        : allShipmentsRes?.data || [];
    }
    if (!shipmentForBulkRes) return [];
    return Array.isArray(shipmentForBulkRes)
      ? shipmentForBulkRes
      : shipmentForBulkRes?.data || [];
  }, [shipmentForBulkRes, allShipmentsRes, selectionMode]);

  React.useEffect(() => {
    if (selectionMode === "vendor") {
      fetchVendors();
    } else if (selectionMode === "customer") {
      fetchCustomers({ Active: "true" });
    } else if (selectionMode === "all") {
      fetchAllShipments();
    }
  }, [fetchVendors, fetchCustomers, fetchAllShipments, selectionMode]);

  React.useEffect(() => {
    setValue("shipments", []);
    setValue("vendor", null);
    setValue("customer", null);
    setValue("reason", "");
    setShowSuccessMessage(false);
  }, [selectionMode, setValue]);

  React.useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  const handleCancelClick = () => {
    if (selectedShipments?.length > 0) {
      setModalOpen(true);
    }
  };

  const handleModalConfirm = async () => {
    const shipmentIds = selectedShipments.map((shipment) => shipment._id);
    const shipmentCount = selectedShipments.length;
    setLastCancellationCount(shipmentCount);
    await onSubmit({
      shipments: shipmentIds,
      reason: reason.trim() || "No reason provided",
    });
    handlePostCancellationCleanup();
    setModalOpen(false);
  };

  const handlePostCancellationCleanup = () => {
    reset(INITIAL_VALUE);
    setShowSuccessMessage(true);
    if (selectionMode === "all") {
      fetchAllShipments();
    } else {
      refetchShipments?.();
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleClearForm = () => {
    reset(INITIAL_VALUE);
    setShowSuccessMessage(false);
    setSelectionMode("all");
  };

  const handleSelectionModeChange = (event, newMode) => {
    if (newMode) {
      setSelectionMode(newMode);
    }
  };

  const isFormReady =
    selectedShipments?.length > 0 &&
    reason?.trim()?.length >= 3 &&
    !isLoadingShipmentForBulk &&
    !fetchingShipmentForBulk &&
    !isLoadingAllShipments &&
    !fetchingAllShipments &&
    !errors.reason;

  const isLoadingData =
    loadingVendors ||
    fetchingVendors ||
    loadingCustomers ||
    fetchingCustomers ||
    isLoadingShipmentForBulk ||
    fetchingShipmentForBulk ||
    isLoadingAllShipments ||
    fetchingAllShipments;

  const isShipmentFieldDisabled =
    (selectionMode === "vendor" && !selectedVendor?._id) ||
    (selectionMode === "customer" && !selectedCustomer?._id);

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {showSuccessMessage && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{
              mb: 3,
              borderRadius: 2,
              bgcolor: theme.palette.success.main,
              color: theme.palette.success.contrastText,
            }}
          >
            <Typography variant="body1" fontWeight={500}>
              Successfully cancelled {lastCancellationCount} shipment(s)!
            </Typography>
            <Typography variant="body2" color={theme.palette.success.contrastText}>
              The shipments have been marked as cancelled and removed from the
              available list.
            </Typography>
          </Alert>
        )}

        <Box mb={2}>
          <ToggleButtonGroup
            value={selectionMode}
            exclusive
            onChange={handleSelectionModeChange}
            aria-label="selection mode"
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                borderColor: theme.palette.divider,
                "&.Mui-selected": {
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    bgcolor: theme.palette.primary.dark,
                  },
                },
              },
            }}
          >
            <ToggleButton value="all" aria-label="all mode">
              <ListIcon sx={{ mr: 1 }} /> All
            </ToggleButton>
            <ToggleButton value="vendor" aria-label="vendor mode">
              <StoreIcon sx={{ mr: 1 }} /> Vendor
            </ToggleButton>
            <ToggleButton value="customer" aria-label="customer mode">
              <PersonIcon sx={{ mr: 1 }} /> Customer
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid container spacing={3}>
          {(selectionMode === "vendor" || selectionMode === "customer") && (
            <Grid item xs={12} md={4}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  {selectionMode === "vendor" ? (
                    <StoreIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  ) : (
                    <PersonIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  )}
                  <Typography variant="subtitle1" fontWeight={600}>
                    Select {selectionMode === "vendor" ? "Vendor" : "Customer"}
                  </Typography>
                </Box>

                <Controller
                  name={selectionMode === "vendor" ? "vendor" : "customer"}
                  control={control}
                  rules={{
                    required: `Please select a ${selectionMode === "vendor" ? "Vendor" : "Customer"}`,
                  }}
                  render={(props) => (
                    <BaseAutocomplete
                      {...formHookInputHelper(props)}
                      fullWidth
                      required
                      label={`Choose ${selectionMode === "vendor" ? "Vendor" : "Customer"}`}
                      placeholder={`Search and select ${selectionMode === "vendor" ? "vendor" : "customer"}...`}
                      options={selectionMode === "vendor" ? vendorList : customerList.data}
                      getOptionLabel={(opt) => {
                        if (!opt) return "";
                        if (selectionMode === "vendor") {
                          return `${opt?.shopName || "Unknown"} - ${opt?.ownerName || "Unknown"}`;
                        }
                        return `${opt?.name || "Unknown"} - ${opt?.phone || "Unknown"}`;
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      onChange={(_, data) => {
                        if (data && isValidObjectId(data._id)) {
                          setValue(selectionMode === "vendor" ? "vendor" : "customer", data);
                        } else {
                          setValue(selectionMode === "vendor" ? "vendor" : "customer", null);
                        }
                      }}
                      loading={selectionMode === "vendor" ? loadingVendors || fetchingVendors : loadingCustomers || fetchingCustomers}
                      loadingText={`Loading ${selectionMode === "vendor" ? "vendors" : "customers"}...`}
                      disabled={isBusy}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.divider,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: theme.palette.text.secondary,
                        },
                      }}
                    />
                  )}
                />

                {(selectedVendor || selectedCustomer) && (
                  <Box mt={1}>
                    <Chip
                      label={
                        selectionMode === "vendor"
                          ? selectedVendor?.shopName
                          : selectedCustomer?.name
                      }
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        borderColor: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={selectionMode === "all" ? 12 : 8}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: theme.palette.background.default,
                color: theme.palette.text.primary,
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <AssignmentIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Select Shipments to Cancel
                </Typography>
              </Box>

              <Controller
                name="shipments"
                control={control}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    multiple
                    label="Choose Shipments"
                    placeholder="Search and select shipments..."
                    loading={isLoadingShipmentForBulk || fetchingShipmentForBulk || isLoadingAllShipments || fetchingAllShipments}
                    loadingText="Loading shipments..."
                    options={shipmentsList}
                    getOptionLabel={(opt) => {
                      if (!opt) return "";
                      return `${opt?.shipmentId}`;
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    value={field?.value ?? []}
                    onChange={(_, data) => {
                      setValue("shipments", data);
                    }}
                    disabled={isBusy || (selectionMode !== "all" && isShipmentFieldDisabled)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.divider,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: theme.palette.text.secondary,
                      },
                    }}
                  />
                )}
              />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: theme.palette.background.default,
                color: theme.palette.text.primary,
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Cancellation Reason
                </Typography>
              </Box>

              <Controller
                name="reason"
                control={control}
                rules={{
                  required: "Please provide a cancellation reason",
                  validate: {
                    minCharacters: (value) => {
                      const charCount = value.trim().length;
                      return charCount >= 3 || "Reason must contain at least 3 characters";
                    },
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Reason for Cancellation"
                    placeholder="Enter reason for cancelling shipments"
                    multiline
                    rows={3}
                    error={!!errors.reason}
                    helperText={errors.reason?.message}
                    disabled={isBusy}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.divider,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: theme.palette.text.secondary,
                      },
                    }}
                  />
                )}
              />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2, bgcolor: theme.palette.divider }} />

            <Box display="flex" justifyContent="center" gap={2}>
              <Button
                onClick={handleClearForm}
                variant="outlined"
                disabled={isBusy || isLoadingData}
                sx={{
                  minWidth: 120,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                Clear Form
              </Button>

              <Button
                onClick={handleCancelClick}
                variant="contained"
                size="large"
                disabled={!isFormReady || isBusy}
                startIcon={isBusy ? null : <WarningIcon />}
                sx={{
                  minWidth: 200,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  bgcolor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  "&:hover": {
                    bgcolor: theme.palette.error.dark,
                  },
                  "&.Mui-disabled": {
                    bgcolor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                {isBusy
                  ? "Processing..."
                  : `Cancel ${selectedShipments?.length || 0} Shipments`}
              </Button>
            </Box>

            <Box mt={2} textAlign="center">
              {isLoadingData && (
                <Typography variant="body2" color={theme.palette.text.secondary}>
                  Loading data...
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        <CancelShipmentModal
          open={modalOpen}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          isLoading={isBusy}
          shipmentCount={selectedShipments?.length || 0}
        />
      </CardContent>
    </Card>
  );
};